import React from 'react';
import { Button } from '@roger/ui-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { RowsPerPageSelect } from '../TablePaginationFooter';
import {
  REPORT_PAGE_SIZE_OPTIONS,
  coerceReportPageSize,
  type ReportPageSizeOption,
} from '../../utils/reportDisplay';

type PaginationBarProps = {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  /** Noun for the count line, e.g. "visits" or "payrolls". */
  itemsLabel: string;
  onPageChange: (page: number) => void;
  /** Shows the "Rows per page" select (25/50/100) when provided. */
  onPageSizeChange?: (pageSize: ReportPageSizeOption) => void;
  disabled?: boolean;
};

/** Intake-Manager-style bottom pagination bar shared by the payroll screens. */
export const PaginationBar: React.FC<PaginationBarProps> = ({
  page,
  totalPages,
  total,
  pageSize,
  itemsLabel,
  onPageChange,
  onPageSizeChange,
  disabled = false,
}) => (
  <div className='flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between'>
    <div className='text-sm text-muted-foreground'>
      Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, total)}{' '}
      of {total} {itemsLabel}
    </div>
    <div className='flex flex-wrap items-center gap-x-4 gap-y-2'>
      {onPageSizeChange && (
        <RowsPerPageSelect
          className='gap-2 text-sm text-muted-foreground'
          pageSize={pageSize}
          options={REPORT_PAGE_SIZE_OPTIONS}
          onPageSizeChange={size =>
            onPageSizeChange(coerceReportPageSize(size))
          }
          disabled={disabled}
        />
      )}
      <div className='flex items-center gap-2'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1 || disabled}
        >
          <ChevronLeft className='h-4 w-4' />
          Previous
        </Button>
        <div className='text-sm px-2'>
          Page {page} of {totalPages}
        </div>
        <Button
          variant='outline'
          size='sm'
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages || disabled}
        >
          Next
          <ChevronRight className='h-4 w-4' />
        </Button>
      </div>
    </div>
  </div>
);
